import { PERMISSION_RESOURCE_CODES as R } from '../../src/permissions/definitions';
import type { AppRoute } from '../routes';

const boundary = '@/modules/zy-hr/EmployeeDomainBoundary';
const page = (
  path: string,
  name: string,
  permissionResourceCode: string,
  component: string,
  icon?: string,
): AppRoute => ({
  path,
  name,
  icon,
  permissionResourceCode,
  component: `@/modules/zy-hr/${component}`,
});
const detail = (
  path: string,
  permissionResourceCode: string,
  component: string,
  parent: string,
): AppRoute => ({
  ...page(path, '', permissionResourceCode, `pages/${component}`),
  hideInMenu: true,
  parentKeys: ['/attendance-center', parent],
});
const configurationDetail = (
  path: string,
  permissionResourceCode: string,
  parent: string,
): AppRoute => ({
  ...detail(
    path,
    permissionResourceCode,
    'AttendanceConfigurationDetailPage',
    parent,
  ),
});

export const attendanceRoutes: AppRoute[] = [
  {
    path: '/attendance-center',
    name: '考勤中心',
    icon: 'schedule',
    wrappers: [boundary],
    routes: [
      {
        path: '/attendance-center/',
        hideInMenu: true,
        component: './Platform/OverviewRedirect',
      },
      page(
        '/attendance-center/overview',
        '概览',
        R.attendanceWorkbench,
        'pages/AttendanceWorkbench',
        'dashboard',
      ),
      page(
        '/attendance-center/approval',
        '我的审批',
        R.attendanceApproval,
        'pages/ApprovalCenter',
        'audit',
      ),
      {
        path: '',
        key: 'attendance-team',
        name: '团队考勤',
        icon: 'team',
        hideInMenu: true,
        routes: [
          {
            path: '/attendance-center/team/',
            hideInMenu: true,
            redirect: '/attendance-center/team/overview',
          },
          page(
            '/attendance-center/team/overview',
            '团队概览',
            R.teamOverview,
            'pages/AttendanceTeamOverview',
          ),
          page(
            '/attendance-center/team/exceptions',
            '团队异常',
            R.teamExceptions,
            'pages/AttendanceTeamExceptions',
          ),
          page(
            '/attendance-center/team/schedules',
            '团队排班',
            R.teamSchedules,
            'pages/AttendanceTeamSchedules',
          ),
          page(
            '/attendance-center/team/monthly',
            '团队月报',
            R.teamMonthly,
            'pages/AttendanceTeamMonthly',
          ),
        ],
      },
      {
        path: '',
        key: 'attendance-requests',
        name: '考勤申请',
        icon: 'form',
        routes: [
          page(
            '/attendance-center/leave-requests',
            '请假申请',
            R.leaveRequests,
            'pages/AttendanceCenterRequestPage',
          ),
          detail(
            '/attendance-center/leave-requests/create',
            R.leaveRequests,
            'AttendanceCenterRequestPage',
            '/attendance-center/leave-requests',
          ),
          detail(
            '/attendance-center/leave-requests/:uuid/edit',
            R.leaveRequests,
            'AttendanceCenterRequestPage',
            '/attendance-center/leave-requests',
          ),
          detail(
            '/attendance-center/leave-requests/:uuid',
            R.leaveRequests,
            'AttendanceRequestDetailPage',
            '/attendance-center/leave-requests',
          ),
          page(
            '/attendance-center/overtime-requests',
            '加班申请',
            R.overtimeRequests,
            'pages/AttendanceCenterRequestPage',
          ),
          detail(
            '/attendance-center/overtime-requests/create',
            R.overtimeRequests,
            'AttendanceCenterRequestPage',
            '/attendance-center/overtime-requests',
          ),
          detail(
            '/attendance-center/overtime-requests/:uuid/edit',
            R.overtimeRequests,
            'AttendanceCenterRequestPage',
            '/attendance-center/overtime-requests',
          ),
          detail(
            '/attendance-center/overtime-requests/:uuid',
            R.overtimeRequests,
            'AttendanceRequestDetailPage',
            '/attendance-center/overtime-requests',
          ),
          page(
            '/attendance-center/punch-requests',
            '补卡申请',
            R.punchRequests,
            'pages/AttendanceCenterRequestPage',
          ),
          detail(
            '/attendance-center/punch-requests/create',
            R.punchRequests,
            'AttendanceCenterRequestPage',
            '/attendance-center/punch-requests',
          ),
          detail(
            '/attendance-center/punch-requests/:uuid/edit',
            R.punchRequests,
            'AttendanceCenterRequestPage',
            '/attendance-center/punch-requests',
          ),
          detail(
            '/attendance-center/punch-requests/:uuid',
            R.punchRequests,
            'AttendanceRequestDetailPage',
            '/attendance-center/punch-requests',
          ),
        ],
      },
      {
        path: '',
        key: 'attendance-processing',
        name: '考勤管理',
        icon: 'database',
        routes: [
          page(
            '/attendance-center/records',
            '打卡记录',
            R.records,
            'pages/AttendanceRecordList',
          ),
          detail(
            '/attendance-center/records/create',
            R.records,
            'AttendanceRecordFormPage',
            '/attendance-center/records',
          ),
          detail(
            '/attendance-center/records/:uuid/edit',
            R.records,
            'AttendanceRecordFormPage',
            '/attendance-center/records',
          ),
          detail(
            '/attendance-center/records/:uuid',
            R.records,
            'AttendanceRecordDetailPage',
            '/attendance-center/records',
          ),
          page(
            '/attendance-center/daily-results',
            '每日考勤',
            R.dailyResults,
            'pages/DailyResultList',
          ),
          page(
            '/attendance-center/monthly-summary',
            '每月考勤',
            R.monthlySummaries,
            'pages/MonthlySummaryList',
          ),
        ],
      },
      {
        path: '',
        key: 'attendance-scheduling',
        name: '排班与规则',
        icon: 'branches',
        routes: [
          page(
            '/attendance-center/shifts',
            '班次管理',
            R.shifts,
            'pages/AttendanceShiftList',
          ),
          detail(
            '/attendance-center/shifts/create',
            R.shifts,
            'AttendanceShiftFormPage',
            '/attendance-center/shifts',
          ),
          detail(
            '/attendance-center/shifts/:uuid/edit',
            R.shifts,
            'AttendanceShiftFormPage',
            '/attendance-center/shifts',
          ),
          configurationDetail(
            '/attendance-center/shifts/:uuid',
            R.shifts,
            '/attendance-center/shifts',
          ),
          page(
            '/attendance-center/rules',
            '考勤规则',
            R.rules,
            'pages/AttendanceRuleList',
          ),
          detail(
            '/attendance-center/rules/create',
            R.rules,
            'AttendanceRuleFormPage',
            '/attendance-center/rules',
          ),
          detail(
            '/attendance-center/rules/:uuid/edit',
            R.rules,
            'AttendanceRuleFormPage',
            '/attendance-center/rules',
          ),
          configurationDetail(
            '/attendance-center/rules/:uuid',
            R.rules,
            '/attendance-center/rules',
          ),
          page(
            '/attendance-center/holiday-plans',
            '节假日方案',
            R.holidayPlans,
            'pages/AttendanceHolidayPlanList',
          ),
          detail(
            '/attendance-center/holiday-plans/create',
            R.holidayPlans,
            'AttendanceHolidayPlanFormPage',
            '/attendance-center/holiday-plans',
          ),
          detail(
            '/attendance-center/holiday-plans/:uuid/edit',
            R.holidayPlans,
            'AttendanceHolidayPlanFormPage',
            '/attendance-center/holiday-plans',
          ),
          configurationDetail(
            '/attendance-center/holiday-plans/:uuid',
            R.holidayPlans,
            '/attendance-center/holiday-plans',
          ),
          page(
            '/attendance-center/groups',
            '考勤组',
            R.groups,
            'pages/AttendanceGroupList',
          ),
          detail(
            '/attendance-center/groups/create',
            R.groups,
            'AttendanceGroupFormPage',
            '/attendance-center/groups',
          ),
          detail(
            '/attendance-center/groups/:uuid/edit',
            R.groups,
            'AttendanceGroupFormPage',
            '/attendance-center/groups',
          ),
          configurationDetail(
            '/attendance-center/groups/:uuid',
            R.groups,
            '/attendance-center/groups',
          ),
          page(
            '/attendance-center/schedules',
            '排班日历',
            R.schedules,
            'pages/AttendanceScheduleCalendar',
          ),
          detail(
            '/attendance-center/schedules/create',
            R.schedules,
            'AttendanceScheduleFormPage',
            '/attendance-center/schedules',
          ),
          detail(
            '/attendance-center/schedules/:uuid/edit',
            R.schedules,
            'AttendanceScheduleFormPage',
            '/attendance-center/schedules',
          ),
          configurationDetail(
            '/attendance-center/schedules/:uuid',
            R.schedules,
            '/attendance-center/schedules',
          ),
        ],
      },
      {
        path: '',
        key: 'attendance-holidays',
        name: '假期管理',
        icon: 'wallet',
        routes: [
          page(
            '/attendance-center/leave-balances',
            '员工假期',
            R.leaveBalances,
            'pages/AttendanceLeaveBalanceList',
          ),
          detail(
            '/attendance-center/leave-balances/create',
            R.leaveBalances,
            'AttendanceLeaveBalanceFormPage',
            '/attendance-center/leave-balances',
          ),
          detail(
            '/attendance-center/leave-balances/employees/:employeeUuid',
            R.leaveBalances,
            'AttendanceEmployeeLeaveBalancePage',
            '/attendance-center/leave-balances',
          ),
          detail(
            '/attendance-center/leave-balances/:uuid/edit',
            R.leaveBalances,
            'AttendanceLeaveBalanceFormPage',
            '/attendance-center/leave-balances',
          ),
          configurationDetail(
            '/attendance-center/leave-balances/:uuid',
            R.leaveBalances,
            '/attendance-center/leave-balances',
          ),
          page(
            '/attendance-center/leave-grant-policies',
            '假期发放规则',
            R.leaveGrantPolicies,
            'pages/AttendanceLeaveGrantPolicyList',
          ),
          detail(
            '/attendance-center/leave-grant-policies/create',
            R.leaveGrantPolicies,
            'AttendanceLeaveGrantPolicyFormPage',
            '/attendance-center/leave-grant-policies',
          ),
          detail(
            '/attendance-center/leave-grant-policies/:uuid/edit',
            R.leaveGrantPolicies,
            'AttendanceLeaveGrantPolicyFormPage',
            '/attendance-center/leave-grant-policies',
          ),
          configurationDetail(
            '/attendance-center/leave-grant-policies/:uuid',
            R.leaveGrantPolicies,
            '/attendance-center/leave-grant-policies',
          ),
        ],
      },
      page(
        '/attendance-center/operation-audits',
        '操作记录',
        R.operationAudits,
        'pages/AttendanceOperationAuditList',
        'history',
      ),
      {
        path: '',
        key: 'attendance-settings',
        name: '考勤设置',
        icon: 'setting',
        routes: [
          {
            path: '/attendance-center/settings/',
            hideInMenu: true,
            redirect: '/attendance-center/settings/automation',
          },
          {
            path: '/attendance-center/settings/general',
            hideInMenu: true,
            redirect: '/attendance-center/settings/automation',
          },
          page(
            '/attendance-center/settings/automation',
            '每日考勤设置',
            R.settings,
            'pages/AttendanceSetting',
          ),
          page(
            '/attendance-center/settings/monthly',
            '每月考勤设置',
            R.settings,
            'pages/AttendanceMonthlySetting',
          ),
          page(
            '/attendance-center/settings/workflow',
            '审批流程设置',
            R.attendanceWorkflowSettings,
            'pages/AttendanceWorkflowSettingsPage',
          ),
          page(
            '/attendance-center/settings/devices',
            '考勤设备',
            R.settings,
            'pages/AttendanceDeviceList',
          ),
        ],
      },
    ],
  },
];
